import { makeStyles } from 'tss-react/mui';
import { P2GBox, P2GCardContainer, P2GFade, P2GGrid, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { BasketItem } from '~/lib/types/basket-types';
import { useMemo } from 'react';
import { toDisplayCurrency } from '~/lib/prices';
import { SumNumber } from '~/lib/utils/array';
import LiveChat from './live-chat';
import useBasket from '~/hooks/use-basket';
import { GenerateBreakDown, GetCurrency } from '~/hooks/use-basket-item-price';
import {
  GetDdpAdminFee,
  GetDdpDutyAndTax,
  GetLocalTaxCost,
  GetProtectionCost,
  GetUnderpayments
} from '~/lib/basket-breakdown';
import Underpayment from './underpayment';
import { useTaxName } from '@parcel2go/npm-p2g-app';

const useStyles = makeStyles()((theme) => ({
  root: {
    margin: theme.spacing(4, -4, -4, -4) + '!important',
    padding: theme.spacing(4) + '!important',
    backgroundColor: theme.palette.grey.A200,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 4, 4) + '!important'
    }
  },
  breakdown: {
    '& > .MuiCardContent-root': {
      padding: theme.spacing(4) + '!important',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3) + '!important'
      }
    }
  },
  breakdownItems: {
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2)
    }
  }
}));

type props = {
  items: { name: string; item: BasketItem }[];
  wrapper: (c: React.ReactNode) => React.ReactNode;
  children: React.ReactNode;
};

const Total = ({ items, wrapper, children }: props) => {
  const { classes } = useStyles();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const taxName = useTaxName();
  const { validation } = useBasket();

  const { total, currency, tax, subTotal, discount, ddpDutiesAndTaxes, underpayment, protection, basePrice, localTax } =
    useMemo(() => {
      if (validation?.breakdown?.total) {
        const currency = GetCurrency(items[0]?.item);
        const d = validation.breakdown.total.discount || 0;
        const ddpDutiesAndTaxes = GetDdpDutyAndTax(validation.breakdown.total.breakdown);
        const ddpAdminFee = GetDdpAdminFee(validation.breakdown.total.breakdown);
        const underpayment = GetUnderpayments(validation.breakdown.total.breakdown);
        const protection = GetProtectionCost(validation.breakdown.total.breakdown);
        const localTax = GetLocalTaxCost(validation.breakdown.total.breakdown);
        const subTotal = validation.breakdown.total.net + d - ddpDutiesAndTaxes;
        const basePrice = subTotal - protection;

        return {
          total: validation.breakdown.total.gross,
          tax: validation.breakdown.total.tax,
          subTotal: subTotal,
          currency: currency,
          discount: d,
          ddpDutiesAndTaxes: ddpDutiesAndTaxes,
          ddpAdminFee: ddpAdminFee,
          underpayment: underpayment,
          protection: protection,
          basePrice: basePrice,
          localTax: localTax
        };
      }

      const mapped = items.map((x) => GenerateBreakDown(x.item));
      return {
        total: SumNumber(mapped.map((x) => x.total)),
        tax: SumNumber(mapped.map((x) => x.tax)),
        subTotal: SumNumber(mapped.map((x) => x.subTotal)),
        currency: mapped[0].quote.currency,
        protection: SumNumber(mapped.map((x) => x.protection)),
        basePrice: SumNumber(mapped.map((x) => x.basePrice)),
        localTax: SumNumber(mapped.map((x) => x.localTax)),
        discount: 0,
        underpayment: 0,
        ddpDutiesAndTaxes: 0,
        ddpAdminFee: 0
      };
    }, [items, validation.breakdown]);

  if (validation.errors) {
    return null;
  }

  return (
    <>
      {underpayment > 0 && <Underpayment currency={currency} underpayment={underpayment} wrapper={wrapper} />}
      {wrapper(
        <P2GCardContainer classNames={[classes.breakdown]}>
          <P2GGrid container spacing={1} alignItems="center">
            <P2GGrid item xs={12} sm={7}>
              <LiveChat variant="BASKET" />
              {children}
            </P2GGrid>
            <P2GGrid item xs={12} sm={5}>
              {validation.validating && (
                <P2GBox textAlign="center">
                  <P2GFade in>
                    <P2GProgress size="large" variant="primary" />
                  </P2GFade>
                </P2GBox>
              )}
              {!validation.validating && (
                <P2GBox textAlign="right" classNames={[classes.breakdownItems]}>
                  <P2GFade in>
                    <P2GGrid container spacing={1} direction="column">
                      {basePrice > 0 && (
                        <P2GGrid item>
                          <P2GGrid container>
                            <P2GGrid item>
                              <P2GText variant="body1">{t('BASKET_BASE', { defaultValue: 'Base Price:' })}</P2GText>
                            </P2GGrid>
                            <P2GGrid item flexGrow={1} />
                            <P2GGrid item>
                              <P2GText variant="body1">{toDisplayCurrency(currency, basePrice, 2)}</P2GText>
                            </P2GGrid>
                          </P2GGrid>
                        </P2GGrid>
                      )}
                      {protection > 0 && (
                        <P2GGrid item>
                          <P2GGrid container>
                            <P2GGrid item>
                              <P2GText variant="body1">
                                {t('BASKET_PROTECTION', { defaultValue: 'Protection:' })}
                              </P2GText>
                            </P2GGrid>
                            <P2GGrid item flexGrow={1} />
                            <P2GGrid item>
                              <P2GText variant="body1">{toDisplayCurrency(currency, protection, 2)}</P2GText>
                            </P2GGrid>
                          </P2GGrid>
                        </P2GGrid>
                      )}
                      <P2GGrid item>
                        <P2GGrid container mt={1} mb={1}>
                          <P2GGrid item>
                            <P2GText variant="body1" fontWeight="bold">
                              {t('BASKET_TOTAL_SUBTOTAL', { defaultValue: 'Sub Total:' })}
                            </P2GText>
                          </P2GGrid>
                          <P2GGrid item flexGrow={1} />
                          <P2GGrid item>
                            <P2GText variant="body1" dataTestId="basket-sub-total" fontWeight="bold">
                              {toDisplayCurrency(currency, subTotal, 2)}
                            </P2GText>
                          </P2GGrid>
                        </P2GGrid>
                      </P2GGrid>
                      {!!discount && discount > 0 && (
                        <P2GGrid item>
                          <P2GGrid container>
                            <P2GGrid item>
                              <P2GText variant="body1">
                                {t('BASKET_TOTAL_DISCOUNT', { defaultValue: 'Discount:' })}
                              </P2GText>
                            </P2GGrid>
                            <P2GGrid item flexGrow={1} />
                            <P2GGrid item>
                              <P2GText variant="body1" dataTestId="basket-discount">
                                {toDisplayCurrency(currency, discount, 2)}
                              </P2GText>
                            </P2GGrid>
                          </P2GGrid>
                        </P2GGrid>
                      )}
                      {tax > 0 && (
                        <P2GGrid item>
                          <P2GGrid container>
                            <P2GGrid item>
                              <P2GText variant="body1">
                                {t('BASKET_TOTAL_TAX', { defaultValue: '{{taxName}}:', taxName: taxName })}
                              </P2GText>
                            </P2GGrid>
                            <P2GGrid item flexGrow={1} />
                            <P2GGrid item>
                              <P2GText variant="body1" dataTestId="basket-tax">
                                {toDisplayCurrency(currency, tax, 2)}
                              </P2GText>
                            </P2GGrid>
                          </P2GGrid>
                        </P2GGrid>
                      )}
                      {ddpDutiesAndTaxes > 0 && (
                        <P2GGrid item>
                          <P2GGrid container>
                            <P2GGrid item>
                              <P2GText variant="body1">
                                {t('BASKET_DDP', { defaultValue: 'Duty and Local Taxes:' })}
                              </P2GText>
                            </P2GGrid>
                            <P2GGrid item flexGrow={1} />
                            <P2GGrid item>
                              <P2GText variant="body1">{toDisplayCurrency(currency, ddpDutiesAndTaxes, 2)}</P2GText>
                            </P2GGrid>
                          </P2GGrid>
                        </P2GGrid>
                      )}
                      {localTax > 0 && (
                        <P2GGrid item>
                          <P2GGrid container>
                            <P2GGrid item>
                              <P2GText variant="body1">{t('BASKET_LOCAL_TAX', { defaultValue: 'Local Tax:' })}</P2GText>
                            </P2GGrid>
                            <P2GGrid item flexGrow={1} />
                            <P2GGrid item>
                              <P2GText variant="body1">{toDisplayCurrency(currency, localTax, 2)}</P2GText>
                            </P2GGrid>
                          </P2GGrid>
                        </P2GGrid>
                      )}
                    </P2GGrid>
                  </P2GFade>
                </P2GBox>
              )}
            </P2GGrid>
          </P2GGrid>
          <P2GBox textAlign="right" classNames={[classes.root]}>
            <P2GGrid container>
              <P2GGrid item>
                <P2GText variant="subtitle2" fontWeight="bold">
                  {t('BASKET_TOTAL', { defaultValue: 'Total:' })}
                </P2GText>
              </P2GGrid>
              <P2GGrid item flexGrow={1} />
              <P2GGrid item>
                <P2GText variant="subtitle2" fontWeight="bold" dataTestId="basket-total">
                  {t('BASKET_TOTAL_VALUE', {
                    defaultValue: '{{value}}',
                    value: toDisplayCurrency(currency, total, 2)
                  })}
                </P2GText>
              </P2GGrid>
            </P2GGrid>
          </P2GBox>
        </P2GCardContainer>
      )}
    </>
  );
};
export default Total;
